import { Injectable } from '@angular/core';

export const TokenKey = 'token';
export const RefreshKey = 'refresh';
export const ExpiresKey = 'expires';
export const KeepLoggedKey = 'keepLoggedIn';

interface LegacySession {
  token: string;
  refresh: string;
  expires: string;
}

@Injectable({ providedIn: 'root' })
export class LegacySessionService {
  private readonly epochKey = 'opogo.legacy.epoch';
  private readonly sessionEpochKey = 'opogo.legacy.sessionEpoch';

  get epoch(): string { return localStorage.getItem(this.epochKey) || 'initial'; }

  get remember(): boolean {
    return (localStorage.getItem(KeepLoggedKey) || sessionStorage.getItem(KeepLoggedKey)) === 'true';
  }

  set remember(value: boolean) {
    (value ? sessionStorage : localStorage).removeItem(KeepLoggedKey);
    (value ? localStorage : sessionStorage).setItem(KeepLoggedKey, String(value));
  }

  get session(): LegacySession | null {
    const storage = this.remember ? localStorage : sessionStorage;
    // Unmarked pre-rollout sessions are accepted only until their first retirement.
    if ((storage.getItem(this.sessionEpochKey) || 'initial') !== this.epoch) return null;
    const token = storage.getItem(TokenKey);
    const refresh = storage.getItem(RefreshKey);
    const expires = storage.getItem(ExpiresKey);
    return token && refresh && expires ? { token, refresh, expires } : null;
  }

  get valid(): boolean {
    const session = this.session;
    return !!session && Date.parse(session.expires) > Date.now();
  }

  save(token: string, refresh: string, expires: string, epoch = this.epoch): void {
    const storage = this.remember ? localStorage : sessionStorage;
    this.clearStorage(this.remember ? sessionStorage : localStorage);
    storage.setItem(TokenKey, token);
    storage.setItem(RefreshKey, refresh);
    storage.setItem(ExpiresKey, expires);
    storage.setItem(this.sessionEpochKey, epoch);
  }

  retire(): void {
    // Other tabs cannot clear each other's sessionStorage. The epoch rejects it
    // on their next request/reload, including tabs that were closed during logout.
    localStorage.setItem(this.epochKey, crypto.randomUUID());
    this.clear();
  }

  clear(): void {
    this.clearStorage(localStorage);
    this.clearStorage(sessionStorage);
  }

  private clearStorage(storage: Storage): void {
    [TokenKey, RefreshKey, ExpiresKey, this.sessionEpochKey, 'authMode'].forEach(key => storage.removeItem(key));
  }
}
